import { DeleteButton, OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomTextarea } from "@components/Forms";
import { CloseUpdateIcon, ConfirmUpdateIcon, DeleteIcon, EditIcon } from "@components/Icons";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmActionModal, ConfirmDeleteModal, CustomSelect, CustomToggle } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { CHARACTER_LENGTH, TOAST } from "@constants/index";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { cellLabels, importanceLevels, severityLevels } from "@constants/vulnerabilityPolicy";
import { PermissionContext } from "@contexts/PermissionContext";
import useExportFileVulnerabilityPolicy from "@hooks/useExportFileVulnerabilityPolicy";
import { useFetchData } from "@hooks/useFetchData";
import useImportFileVulnerabilityPolicy from "@hooks/useImportFileVulnerabilityPolicy";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { getColorByVulnerabilityLevel, isNotNullAndEmptyArray } from "@utils/common";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import AddVulnerabilityPolicyModal from "./AddVulnerabilityPolicyModal";
import VulnerabilityLevelOverview from "./VulnerabilityLevelOverview";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";

const VulnerabilityPolicyList = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { colors } = useTheme();
  const fileInputRef = useRef(null);
  const { canEditVulnerabilityPolicy } = useContext(PermissionContext);
  const isReadOnly = !canEditVulnerabilityPolicy();

  // State management
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteType, setDeleteType] = useState(""); // "single" or "multiple"
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Inline editing state
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // API calls
  const {
    data: vulnerabilityPoliciesResponse,
    isLoading: isLoadingPolicies,
    refetch: refetchPolicies,
  } = useFetchData([queryKeys.getVulnerabilityPolicies], "/vulnerability_policy", { method: "GET" });

  // Extract data from new response format
  // If vulnerabilityPolicies exists, use it; otherwise check if response is an array
  const vulnerabilityPoliciesData = vulnerabilityPoliciesResponse?.vulnerabilityPolicies
    || (Array.isArray(vulnerabilityPoliciesResponse) ? vulnerabilityPoliciesResponse : []);
  const isEditedVulnerabilityPolicy = vulnerabilityPoliciesResponse?.isEdited || false;
  // Export and Import hooks
  const { exportFile, isLoading: isExporting } = useExportFileVulnerabilityPolicy();
  const { importFile, isLoading: isImporting } = useImportFileVulnerabilityPolicy();

  // Update mutation for inline editing
  const updatePolicyMutation = useMutationData(
    () => `/vulnerability_policy`,
    { method: "PUT" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("vulnerability-policy.vulnerability-policy-update-success"));
        refetchPolicies();
        handleCancelInlineEdit();
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          showErrorToast(t("status.error"), t("vulnerability-policy.vulnerability-policy-delete-error-conflict"));
          return;
        }
        ToastConstant(TOAST.error, t("vulnerability-policy.vulnerability-policy-update-error"));
        console.error("Update error:", error);
      },
    },
  );

  // Delete mutations
  const deleteSinglePolicyMutation = useMutationData(
    `/vulnerability_policy/${selectedPolicy?.vulnerabilityPolicySeq}`,
    { method: "DELETE" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("vulnerability-policy.vulnerability-policy-delete-success"));
        refetchPolicies();
        setShowDeleteModal(false);
        setDeleteLoading(false);
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          showErrorToast(t("status.error"), t("vulnerability-policy.vulnerability-policies-delete-error-conflict", { levelCode: error?.response?.data }));
        } else {
          ToastConstant(TOAST.error, t("vulnerability-policy.vulnerability-policy-delete-error"));
          console.error("Delete error:", error);
        }
        setDeleteLoading(false);
        setShowDeleteModal(false);
      },
    },
  );

  const deleteMultiplePoliciesMutation = useMutationData(
    "/vulnerability_policies",
    { method: "DELETE" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("vulnerability-policy.vulnerability-policies-delete-success"));
        refetchPolicies();
        setSelectedRows([]);
        setShowDeleteModal(false);
        setDeleteLoading(false);
      },
      onError: (error) => {
        if (isNotNullAndEmptyArray(error.response.data?.deletedSeqs)) {
          showSuccessToast(error.response.data.deletedSeqs.length + t("vulnerability-policy.vulnerability-policies-delete-success"));
          setSelectedRows([]);
        }
        if (error.response?.status === 409) {
          showErrorToast(t("status.error"), t("vulnerability-policy.vulnerability-policies-delete-error-conflict", { levelCode: error?.response?.data?.message }));
        } else {
          ToastConstant(TOAST.error, t("vulnerability-policy.vulnerability-policies-delete-error"));
          console.error("Delete error:", error);
        }
        setDeleteLoading(false);
        setShowDeleteModal(false);
        refetchPolicies();
      },
    },
  );

  const resetDefaultVulnerabilityPolicy = useMutationData(
    "/vulnerability_policies/reset",
    { method: "PUT" },
    {
      onSuccess: (response) => {
        ToastConstant(TOAST.success, t("vulnerability-policy.reset-default-vulnerability-policy-success"));
        queryClient.setQueryData([queryKeys.getVulnerabilityPolicies], response);
      },
      onError: (error) => {
        ToastConstant(TOAST.error, t("vulnerability-policy.reset-default-vulnerability-policy-error"));
        console.error("Delete error:", error);
      },
    },
  );

  // Inline editing handlers
  const handleInlineEdit = (row) => {
    if (isReadOnly) return;
    setEditingRow(row.vulnerabilityPolicySeq);
    setEditFormData({
      vulnerabilityLevel: row.vulnerabilityLevel ? parseInt(row.vulnerabilityLevel, 10) : 1,
      importanceLevelCd: row.importanceLevelCd?.trim() || "",
      importanceLevelCdName: row.importanceLevelCdName?.trim() || "",
      weaknessCd: row.weaknessCd?.trim() || "",
      weaknessCdName: row.weaknessCdName?.trim() || "",
      useFlag: row.useFlag?.trim() || "N",
      followupContent: row.followupContent?.trim() || "",
    });
  };

  const handleCancelInlineEdit = () => {
    setEditingRow(null);
    setEditFormData({});
  };

  const handleSaveInlineEdit = () => {
    if (!editingRow) return;

    const rowData = vulnerabilityPoliciesData.find((row) => row.vulnerabilityPolicySeq === editingRow);
    if (!rowData) return;

    // Check for duplicate level code (excluding the current row being edited)
    const isDuplicate = vulnerabilityPoliciesData.some((policy) => {
      // Skip the current row being edited
      if (policy.vulnerabilityPolicySeq === editingRow) return false;

      // Check if any other policy has the same importance level and severity combination
      return (
        policy.importanceLevelCd === editFormData.importanceLevelCd &&
        policy.weaknessCd === editFormData.weaknessCd
      );
    });

    if (isDuplicate) {
      showErrorToast(t("status.error"), t("vulnerability-policy.duplication-level-code-error"));
      return;
    }

    const updateData = {
      vulnerabilityPolicySeq: editingRow,
      ...rowData,
      ...editFormData,
    };

    const editKey = `${editFormData.importanceLevelCd}_${editFormData.weaknessCd}`.toUpperCase();
    const editLevelInfo = cellLabels[editKey];
    updateData.levelCode = editLevelInfo?.label || rowData?.levelCode;

    updatePolicyMutation.mutate(updateData);
  };

  const handleInlineInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Prepare options for dropdowns
  const importanceLevelOptions = importanceLevels.map((level) => ({
    value: level,
    label: t(`vulnerability-policy.importance-level-${level?.toLowerCase() || ''}`, level),
  }));

  const severityLevelOptions = severityLevels.map((level) => ({
    value: level,
    label: level?.toLowerCase(),
  }));

  // Table columns configuration
  const baseColumns = [
    "vulnerabilityLevel",
    "importanceLevelCdName",
    "weaknessCdName",
    "levelCode",
    "useFlag",
    "followupContent",
  ];
  const columns = isReadOnly ? baseColumns : [...baseColumns, "actions"];

  const columnConfig = {
    vulnerabilityLevel: {
      style: {
        width: "85px",
        minWidth: "70px",
      },
      headerRender: () => t("vulnerability-policy.vulnerability-level"),
      customRender: (value, row) => {
        if (editingRow === row.vulnerabilityPolicySeq) {
          return (
            <div className="relative">
              <CustomSelect
                options={[
                  { value: 1, label: "1" },
                  { value: 2, label: "2" },
                  { value: 3, label: "3" },
                  { value: 4, label: "4" },
                ]}
                value={editFormData.vulnerabilityLevel ? { value: editFormData.vulnerabilityLevel, label: editFormData.vulnerabilityLevel.toString() } : null}
                onChange={(option) => handleInlineInputChange("vulnerabilityLevel", option?.value || 1)}
                placeholder={t("vulnerability-policy.select-level")}
                size="sm"
                usePortal={true}
                clearable={false}
                isSearchable={false}
              />
            </div>
          );
        }
        return value;
      },
    },
    importanceLevelCdName: {
      style: {
        width: "120px",
        minWidth: "90px",
      },
      headerRender: () => t("vulnerability-policy.importance-level"),
      customRender: (value, row) => {
        if (editingRow === row.vulnerabilityPolicySeq) {
          const importanceLevelEditing = editFormData.importanceLevelCd
          const importanceLevelNameEditing = editFormData.importanceLevelCdName

          return (
            <div className="relative">
              <CustomSelect
                options={importanceLevelOptions}
                value={
                  importanceLevelEditing ? {
                    value: importanceLevelEditing,
                    label: t(`vulnerability-policy.importance-level-${importanceLevelNameEditing?.toLowerCase() || ''}`, importanceLevelNameEditing)
                  } : null}
                onChange={(option) => {
                  handleInlineInputChange("importanceLevelCd", option?.value || "");
                  handleInlineInputChange("importanceLevelCdName", option?.label || "");
                }}
                placeholder="Select Importance Level"
                size="sm"
                clearable={false}
                isSearchable={false}
                menuPlacement="auto"
                usePortal={true}
              />
            </div>
          );
        }

        return t(`vulnerability-policy.importance-level-${value?.toLowerCase() || ''}`, value);
      },
    },
    weaknessCdName: {
      style: {
        width: "100px",
        minWidth: "50px",
      },
      headerRender: () => t("vulnerability-policy.severity"),
      customRender: (value, row) => {
        if (editingRow === row.vulnerabilityPolicySeq) {
          const weaknessCdEditing = editFormData.weaknessCd
          const weaknessCdNameEditing = editFormData.weaknessCdName

          return (
            <div className="relative">
              <CustomSelect
                options={severityLevelOptions}
                value={
                  weaknessCdEditing ? {
                    value: weaknessCdEditing,
                    label: weaknessCdNameEditing
                  } : null}
                onChange={(option) => {
                  handleInlineInputChange("weaknessCd", option?.value || "");
                  handleInlineInputChange("weaknessCdName", option?.label || "");
                }}
                placeholder="Select Severity"
                size="sm"
                clearable={false}
                isSearchable={false}
                usePortal={true}
                customStyles={{
                  option: (base) => ({
                    ...base,
                    textTransform: 'capitalize',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    textTransform: 'capitalize',
                  }),
                }}
              />
            </div>
          );
        }
        return <span className="capitalize">{value}</span>
      },
    },
    levelCode: {
      style: {
        width: "90px",
        minWidth: "80px",
        textAlign: "center",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("vulnerability-policy.vulnerability-security-code"),
      customRender: (value, row) => {
        const key = `${row.importanceLevelCd}_${row.weaknessCd}`.toUpperCase();
        const levelInfo = cellLabels[key];
        if (editingRow === row.vulnerabilityPolicySeq) {
          // In edit mode, calculate the level code based on current form data
          const editKey = `${editFormData.importanceLevelCd}_${editFormData.weaknessCd}`.toUpperCase();
          const editLevelInfo = cellLabels[editKey];

          return (
            <span
              className={`inline-flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-extrabold text-sm`}
              style={{ backgroundColor: getColorByVulnerabilityLevel(editFormData.vulnerabilityLevel) || "#808080" }}
            >
              {editLevelInfo?.label || "?"}
            </span>
          );
        }

        // Normal display mode
        return (
          <span
            className={`inline-flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-extrabold text-sm`}
            style={{ backgroundColor: getColorByVulnerabilityLevel(row.vulnerabilityLevel) || "#808080" }}
          >
            {levelInfo?.label || value}
          </span>
        );
      },
    },
    useFlag: {
      style: {
        width: "100px",
        minWidth: "80px",
        textAlign: "center",
      },
      headerRender: () => t("vulnerability-policy.use-flag"),
      customRender: (value, row) => {
        if (editingRow === row.vulnerabilityPolicySeq) {
          return (
            <CustomToggle
              value={editFormData.useFlag}
              onChange={(newValue) => handleInlineInputChange("useFlag", newValue)}
              className="justify-center"
            />
          );
        }
        return value === "Y" ? t('common.yes') : value === "N" ? t('common.no') : value;
      },
    },
    followupContent: {
      style: {
        width: "280px",
        minWidth: "280px",
      },
      headerRender: () => t("vulnerability-policy.followup-content"),
      customRender: (value, row) => {
        if (editingRow === row.vulnerabilityPolicySeq) {
          return (
            <div className="min-w-[280px] py-1">
              <CustomTextarea
                rows={4}
                value={editFormData.followupContent || ""}
                onChange={(e) => handleInlineInputChange("followupContent", e.target.value)}
                title={editFormData.followupContent || ""}
                className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                maxLength={CHARACTER_LENGTH.COMMENT}
              />
              <p className={`text-xs ${colors.textCharacterCount} mt-1`}>
                {(editFormData.followupContent || "").length}/{CHARACTER_LENGTH.COMMENT}
              </p>
            </div>
          );
        }

        return (
          <div className="min-w-0 overflow-hidden">
            <p
              className={`text-sm ${colors.textPrimary} truncate`}
              title={value || "---"}
            >
              {value || "---"}
            </p>
          </div>
        );
      },
    },
    actions: {
      style: {
        width: "100px",
        minWidth: "50px",
      },
      headerRender: () => (isReadOnly ? "" : t("common.action")),
      customRender: (_, row) => {
        if (isReadOnly) return null;

        if (editingRow === row.vulnerabilityPolicySeq) {
          return (
            <div className="flex items-center space-x-1">
              <button
                onClick={handleSaveInlineEdit}
                disabled={updatePolicyMutation.isPending}
                className={`p-1 rounded ${colors.textActive}`}
                title={t("common.save-button")}
              >
                <ConfirmUpdateIcon className="h-6 w-6" />
              </button>
              <div className={`w-px h-3 ${colors.bgCommunicationFilterButton}`}></div>
              <button
                onClick={handleCancelInlineEdit}
                disabled={updatePolicyMutation.isPending}
                className="p-1 rounded text-red-600"
                title={t("common.cancel-button")}
              >
                <CloseUpdateIcon className="h-6 w-6" />
              </button>
            </div>
          );
        }

        return (
          <div className="flex items-center space-x-1">
            <button
              onClick={() => handleInlineEdit(row)}
              className={`p-1 rounded ${colors.textActive} ${colors.hover}`}
              title={t("common.edit-button")}
            >
              <EditIcon className="h-6 w-6" />
            </button>
            <div className={`w-px h-3 ${colors.bgCommunicationFilterButton}`}></div>
            <button
              onClick={() => handleDeleteSingle(row)}
              className="p-1 rounded text-red-600"
              title={t("common.delete-button")}
            >
              <DeleteIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  };

  const handleDeleteSingle = (policy) => {
    if (isReadOnly) return;
    setSelectedPolicy(policy);
    setDeleteType("single");
    setShowDeleteModal(true);
  };

  const handleDeleteMultiple = () => {
    if (isReadOnly || selectedRows.length === 0) return;
    setDeleteType("multiple");
    setShowDeleteModal(true);
  };

  const handleResetDefaultVulnerabilityPolicy = () => {
    setShowResetConfirm(true);
  };

  const handleConfirmReset = async () => {
    setShowResetConfirm(false);
    await resetDefaultVulnerabilityPolicy.mutateAsync();
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    refetchPolicies();
  };

  const handleCheckboxChange = (newSelectedRows) => {
    if (isReadOnly) return;
    setSelectedRows([...newSelectedRows]);
  };

  // Export handler
  const handleExport = async () => {
    if (isReadOnly) return;
    try {
      await exportFile();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Import handler
  const handleImport = () => {
    if (isReadOnly) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        await importFile(file);
        refetchPolicies(); // Refresh data after successful import
      } catch (error) {
        console.error("Import failed:", error);
      }
      // Reset file input
      event.target.value = "";
    }
  };

  // Delete confirmation handlers
  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleteType === "single") {
        deleteSinglePolicyMutation.mutate();
      } else if (deleteType === "multiple") {
        const bodyRequest = { ids: selectedRows };
        deleteMultiplePoliciesMutation.mutate(bodyRequest);
      }
    } catch (error) {
      console.error("Error deleting policies:", error);
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedPolicy(null);
    setDeleteType("");
  };

  // Prepare data for table
  const tableData = vulnerabilityPoliciesData || [];

  return (
    <div data-testid="vulnerability-policy-list-page" className={`min-h-full ${colors.bgSecondary}`}>
      {/* Header with Breadcrumbs */}
      <div className="pb-5">
        <div className="space-y-3">
          <div>
            <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>{t("vulnerability-policy.title")}</h1>
          </div>
        </div>
      </div>

      {/* Main Content - Table and Overview Side by Side */}
      <div className="grid grid-cols-1 2xl:grid-cols-4 gap-6">
        {/* Table Section - Takes 3/4 of the space on larger screens */}
        <div className="2xl:col-span-3">
          <div
            className={`rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}
            style={{ overflow: "visible" }}
          >
            <div className="flex items-center justify-between pb-4">
              <h3 className={`text-lg font-medium ${colors.textPrimary}`}>
                {t("vulnerability-policy.vulnerability-list-title")}
              </h3>
              {!isReadOnly && (
                <div className="flex items-center space-x-3">
                  <PrimaryButton onClick={handleResetDefaultVulnerabilityPolicy} disabled={!isEditedVulnerabilityPolicy || isReadOnly} size="md">
                    {t("common.reset")}
                  </PrimaryButton>
                  <DeleteButton onClick={handleDeleteMultiple} disabled={selectedRows.length === 0} size="md">
                    {t("common.delete-button")}
                  </DeleteButton>
                  <OutlineButton onClick={handleImport} size="md" disabled={isImporting}>
                    {isImporting ? "Importing..." : t("common.import-button")}
                  </OutlineButton>
                  <OutlineButton onClick={handleExport} size="md" disabled={isExporting}>
                    {isExporting ? "Exporting..." : t("common.export-button")}
                  </OutlineButton>
                  <PrimaryButton data-testid="vulnerability-policy-list-add-button" onClick={() => setShowAddModal(true)} size="md">
                    {t("vulnerability-policy.add-vulnerability-level")}
                  </PrimaryButton>
                </div>
              )}
            </div>

            <div className="flex-grow min-h-0" data-testid="vulnerability-policy-list-table">
              <TableWithPagination
                columns={columns}
                columnConfig={columnConfig}
                data={tableData}
                loading={isLoadingPolicies}
                hasCheckbox={!isReadOnly}
                selectedRows={selectedRows}
                onCheckboxChange={handleCheckboxChange}
                rowKey="vulnerabilityPolicySeq"
                onRowClick={() => { }}
                showPagination={false}
              />
            </div>
          </div>
        </div>

        {/* Vulnerability Level Overview - Takes 1/4 of the space on larger screens */}
        <div className="2xl:col-span-1">
          <div className="sticky top-4">
            <VulnerabilityLevelOverview data={vulnerabilityPoliciesData} />
          </div>
        </div>
      </div>

      {/* Hidden file input for import */}
      {!isReadOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {!isReadOnly && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title={
            deleteType === "single"
              ? t("vulnerability-policy.deletePolicyConfirmationTitle", "Delete Vulnerability Policy")
              : t("vulnerability-policy.deletePoliciesConfirmationTitle", "Delete Vulnerability Policies")
          }
          description={
            deleteType === "single"
              ? t(
                "vulnerability-policy.deletePolicyConfirmationDescription",
                "Are you sure you want to delete this vulnerability policy? This action cannot be undone.",
              )
              : `${t("vulnerability-policy.deletePoliciesConfirmationDescription", "Are you sure you want to delete")} ${selectedRows.length} ${t("vulnerability-policy.deletePoliciesConfirmationDescriptionCount", "vulnerability policies")}? ${t("vulnerability-policy.deletePoliciesConfirmationDescriptionCannotBeUndone", "This action cannot be undone.")}`
          }
          loading={deleteLoading}
        />
      )}

      {/* Add Modal */}
      {!isReadOnly && (
        <AddVulnerabilityPolicyModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
          existingPolicies={vulnerabilityPoliciesData || []}
        />
      )}

      {/* Reset Confirmation Modal */}
      {!isReadOnly && (
        <ConfirmActionModal
          isOpen={showResetConfirm}
          onClose={() => setShowResetConfirm(false)}
          onConfirm={handleConfirmReset}
          title={t("vulnerability-policy.confirm-reset-title")}
          description={t("vulnerability-policy.confirm-reset-description")}
          confirmText={t("common.reset")}
          loading={resetDefaultVulnerabilityPolicy.isPending}
        />
      )}
    </div>
  );
};

export default VulnerabilityPolicyList;
